import { DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CadObject } from '../../../models/cad-object.model';
import { PullDirection } from '../../../models/pull-direction.model';
import { CadViewerComponent } from '../../cad-viewer/cad-viewer.component';
import { DraftAnalysisResult } from '../../cad-viewer/helpers/draft-analysis';
import { CadObjectTreeComponent } from '../../shared/cad-object-tree/cad-object-tree.component';
import { Checkbox } from 'primeng/checkbox';
import { InputNumber } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-pull-directions',
  standalone: true,
  imports: [
    CadViewerComponent,
    DecimalPipe,
    FormsModule,
    Checkbox,
    InputNumber,
    CadObjectTreeComponent,
    TooltipModule,
  ],
  templateUrl: './pull-directions.component.html',
})
export class PullDirectionsComponent {
  @ViewChild(CadViewerComponent) cadViewer!: CadViewerComponent;

  @Input({ required: true }) cadObjects: CadObject[] = [];
  @Input() primaryCadObjectId: number | null = null;
  @Input({ required: true }) pullDirections: PullDirection[] = [];
  @Input() filename = '';
  @Input() showHeader = true;
  @Input() showFindPullDirection = false;
  @Input() findPullDirectionLoading = false;
  @Input() findPullDirectionDisabled = false;
  @Input() findPullDirectionTooltip: string | null = null;
  @Input() cadFileId: number | null = null;

  @Input()
  get selectedPullDirection(): PullDirection | null {
    return this._selectedPullDirection;
  }
  set selectedPullDirection(dir: PullDirection | null) {
    this._selectedPullDirection = dir;
    this.editX = dir?.x ?? 0;
    this.editY = dir?.y ?? 0;
    this.editZ = dir?.z ?? 0;
  }
  private _selectedPullDirection: PullDirection | null = null;

  @Output() pullDirectionSelected = new EventEmitter<PullDirection>();
  @Output() findPullDirection = new EventEmitter<void>();

  editX = 0;
  editY = 0;
  editZ = 0;
  showDraftAnalysis = false;
  maxDraftAngle = 2.0;
  draftResult: DraftAnalysisResult = {
    draftArea: 0,
    visibleArea: 0,
    hiddenArea: 0,
  };
  hoveredCadObjectId: number | null = null;
  visibleCadObjectIds: Set<number> | null = null;

  isSelected(dir: PullDirection): boolean {
    if (!this.selectedPullDirection) return false;
    return (
      this.selectedPullDirection.x === dir.x &&
      this.selectedPullDirection.y === dir.y &&
      this.selectedPullDirection.z === dir.z
    );
  }

  onSelect(dir: PullDirection): void {
    this.pullDirectionSelected.emit(dir);
  }

  onFaceDirectionPicked(dir: PullDirection): void {
    this.pullDirectionSelected.emit(dir);
  }

  onDraftAnalysisResult(result: DraftAnalysisResult): void {
    this.draftResult = result;
  }

  onEditX(val: number): void {
    this.editX = val;
    this.emitEditedDirection();
  }

  onEditY(val: number): void {
    this.editY = val;
    this.emitEditedDirection();
  }

  onEditZ(val: number): void {
    this.editZ = val;
    this.emitEditedDirection();
  }

  private emitEditedDirection(): void {
    this.pullDirectionSelected.emit({
      x: this.editX,
      y: this.editY,
      z: this.editZ,
    });
  }

  useCameraDirection(): void {
    if (!this.cadViewer) return;
    const dir = this.cadViewer.getCameraDirection();
    this.pullDirectionSelected.emit({
      x: -dir.x,
      y: -dir.y,
      z: -dir.z,
    });
  }
}
